import { Box, deepMerge, Text } from '@mantine/core';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultItem } from '../../data-access/types/results';
import { GesStatusValue } from '../../utils/ges-status';
import {
  formatMetricCell,
  formatThresholdRange,
} from '../../utils/format-metrics';

import { GesStatusIcon } from '../ges-status-icon';
import { StackedLabelsCell, StackedValuesCell } from './stacked-table-cells';

import { filterOrganizationColumns, usePermissions } from '@auth/data-access';
import { useFiltersTableOptions } from '@data-access';
import {
  formatOrganizationNames,
  OrganizationsList,
  PfTable,
  TsrButtonLink,
} from '@ui';

export interface ResultsTableProps {
  data: ResultItem[];
  rowCount: number;
  isFetching: boolean;
  isPlaceholderData: boolean;
}

export const ResultsTable: FC<ResultsTableProps> = ({
  data,
  rowCount,
  isFetching,
  isPlaceholderData,
}) => {
  const { t } = useTranslation();
  const { showOrganizationColumn } = usePermissions();
  const filtersTableOptions = useFiltersTableOptions<ResultItem>('results');

  const columns = useMemo<MRT_ColumnDef<ResultItem>[]>(
    () =>
      filterOrganizationColumns(
        [
          {
            accessorKey: 'farm.name',
            header: t('common.societe'),
            Cell: ({ row }) => {
              const label = row.original.farm?.name ?? '-';
              return (
                <TsrButtonLink
                  fullWidth
                  justify="start"
                  maw={250}
                  ml="calc(var(--button-padding-x-compact-md) * -1)"
                  params={{ id: row.original.id.toString() }}
                  size="compact-md"
                  td="underline"
                  to="/results/$id/table"
                  tt="capitalize"
                  variant="subtle"
                >
                  <Text span truncate="end">
                    {label}
                  </Text>
                </TsrButtonLink>
              );
            },
          },
          {
            id: 'organizations',
            accessorFn: (row) => formatOrganizationNames(row.organizations),
            header: t('common.properties.organization'),
            Cell: ({ row }) => (
              <OrganizationsList organizations={row.original.organizations} />
            ),
          },
          {
            id: 'result_type',
            header: '',
            enableColumnFilter: false,
            enableSorting: false,
            size: 100,
            Cell: ({ row }) => (
              <StackedLabelsCell
                labels={[
                  `CSR ${row.original.csr_year_reference ?? '-'}`,
                  `CSA ${row.original.csa_year_reference ?? '-'}`,
                ]}
              />
            ),
          },
          {
            id: 'soil_density',
            header: t('common.densite-du-sol'),
            enableColumnFilter: false,
            Cell: ({ row }) => (
              <StackedValuesCell
                values={[
                  formatMetricCell(row.original.csr_soil_density, 'g/cm³'),
                  formatMetricCell(row.original.csa_soil_density, 'g/cm³'),
                ]}
              />
            ),
          },
          {
            id: 'carbon_stock',
            header: t('common.carbon_stock'),
            enableColumnFilter: false,
            Cell: ({ row }) => (
              <StackedValuesCell
                values={[
                  formatMetricCell(row.original.csr_carbon_stock, 'tC/ha'),
                  formatMetricCell(row.original.csa_carbon_stock, 'tC/ha'),
                ]}
              />
            ),
          },
          {
            id: 'threshold_range',
            header: `${t('common.lower_carbon_stock_threshold')} - ${t('common.upper_carbon_stock_threshold')}`,
            enableColumnFilter: false,
            Cell: ({ row }) => (
              <StackedValuesCell
                values={[
                  formatThresholdRange(
                    row.original.csr_lower_carbon_stock_threshold,
                    row.original.csr_upper_carbon_stock_threshold,
                  ),
                  formatThresholdRange(
                    row.original.csa_lower_carbon_stock_threshold,
                    row.original.csa_upper_carbon_stock_threshold,
                  ),
                ]}
              />
            ),
          },
          {
            accessorKey: 'ges_status',
            enableColumnFilter: false,
            enableSorting: false,
            header: t('table.columns.status'),
            maxSize: 80,
            Cell: ({ cell }) => (
              <GesStatusIcon value={cell.getValue() as GesStatusValue} />
            ),
          },
        ],
        showOrganizationColumn('list'),
      ),
    [t, showOrganizationColumn],
  );

  const tableOptions: MRT_TableOptions<ResultItem> = deepMerge(
    {
      data,
      rowCount,
      columns,

      enableColumnFilters: false,
      enableFullScreenToggle: false,
      enableTopToolbar: false,

      state: {
        showLoadingOverlay: isFetching && isPlaceholderData,
      },
    },
    filtersTableOptions,
  );

  return (
    <Box h="100%" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PfTable<ResultItem> enableShowHideColumns={false} options={tableOptions} />
    </Box>
  );
};

